import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

// Scenario data
const scenarios = [
  {
    category: 'Authentication & Account',
    items: [
      {
        id: 1,
        question: 'I want to check my account balance',
        expected: '• Authenticate customer using phone number\n• Check service status\n• Provide exact balance clearly (digit-by-digit or zero dollars)\n• Offer portal link if balance is owed'
      },
      {
        id: 2,
        question: 'Can you tell me what my account number is?',
        expected: '• Authenticate customer\n• Provide account number digit-by-digit clearly\n• Ask if they need anything else'
      },
      {
        id: 3,
        question: "I'm calling from a different phone, can you still help me?",
        expected: '• Explain authentication requirement politely\n• Ask for customer number or name to locate account\n• If can\'t authenticate: Offer alternatives (call from registered number, contact during business hours)\n• CRITICAL: Should NOT provide account-specific help without authentication'
      },
      {
        id: 4,
        question: 'What services do I have on my account?',
        expected: '• Authenticate customer\n• Retrieve package information\n• List services clearly (internet speed, phone, TV if applicable)\n• Offer more details if needed'
      },
      {
        id: 5,
        question: 'When is my next bill due?',
        expected: '• Authenticate customer\n• Check account info for last bill date\n• Provide bill cycle information clearly\n• Offer portal link to view full billing details'
      }
    ]
  },
  {
    category: 'Internet - No Connection',
    items: [
      {
        id: 6,
        question: "My internet isn't working at all",
        expected: '• Authenticate customer\n• Check service status (not suspended)\n• Identify modem type\n• Guide through appropriate reboot (DSL or Cable specific)\n• Confirm resolution after reboot completes'
      },
      {
        id: 7,
        question: 'None of my lights are on my modem',
        expected: '• Identify power problem\n• Check if modem is plugged in\n• Check power button on side/back\n• Try different power outlet\n• If persists: May be defective equipment, offer escalation'
      },
      {
        id: 8,
        question: 'My DSL light is flashing red',
        expected: '• Identify DSL sync issue (DSL modem)\n• Check phone line connection to DSL port and wall jack\n• Try modem reboot if connections are good\n• If persists: "This might be a line issue. Our team can check during business hours 9am-9pm"'
      },
      {
        id: 9,
        question: "I just moved my modem to a different room and now internet doesn't work",
        expected: '• For DSL: Check phone line plugged into DSL port and wall jack\n• For Cable: Check coax cable connected to wall outlet\n• Make sure connections are tight/secure\n• Guide through modem reboot after confirming connections\n• Confirm resolution'
      },
      {
        id: 10,
        question: "My internet was working fine yesterday, now it's completely down",
        expected: '• Authenticate and check service status\n• If suspended: Route to billing resolution (portal payment)\n• If active: Guide through modem reboot\n• Check lights after reboot\n• If unresolved: Check for outages, escalate if needed'
      },
      {
        id: 11,
        question: 'The internet light on my modem is red',
        expected: '• Check service status first\n• If active: Try modem reboot\n• Wait for full restart (3-5 minutes)\n• If still red after reboot: "This might need our technical team to check. They\'re available 9am-9pm"'
      },
      {
        id: 12,
        question: "My Vilo mesh system isn't working",
        expected: '• Check if main modem has internet first\n• Ask about modem lights status\n• If modem is good: Check Vilo plugged into WAN/Internet port (not just LAN)\n• Try rebooting modem first (wait 2 min), then Vilo'
      },
      {
        id: 13,
        question: 'I unplugged everything and plugged it back in but still no internet',
        expected: '• "Let\'s make sure we do a proper reboot with the right timing"\n• Guide through: Unplug 30 seconds, plug back, wait 3-5 minutes for full restart\n• Check lights after complete restart\n• If still not working: Escalate or check service status again'
      },
      {
        id: 14,
        question: 'My internet goes out every time it rains',
        expected: '• Identify pattern: Weather-related issues = line problem\n• For DSL: "This sounds like moisture in the phone line"\n• For Cable: "This could be a cable line issue"\n• "Our technical team should inspect the outside line. They\'re available 9am-9pm to schedule a visit"'
      },
      {
        id: 15,
        question: 'Internet worked fine until the power came back on after an outage',
        expected: '• Explain that power outages can affect the modem\n• "Let\'s do a proper reboot to reset everything"\n• Guide through full reboot process with proper timing\n• Check if lights come back properly\n• Confirm internet is working'
      }
    ]
  },
  {
    category: 'Internet - Slow Speeds',
    items: [
      {
        id: 16,
        question: 'My internet is really slow',
        expected: '• Authenticate, check service status\n• Ask: "Are you connected via WiFi or ethernet cable?"\n• If WiFi: Check distance from modem, interference, number of devices\n• If Ethernet: Check modem lights, try reboot\n• Suggest speed test after troubleshooting'
      },
      {
        id: 17,
        question: 'Netflix keeps buffering',
        expected: '• Check service status\n• Ask if other devices work fine (isolate device vs internet issue)\n• If only one device: "Try restarting that device"\n• If all devices: Check internet connection, guide through modem reboot\n• Offer WiFi app for better troubleshooting'
      },
      {
        id: 18,
        question: 'My WiFi is slow in the bedroom but fine in the living room',
        expected: '• Identify: WiFi coverage issue, not internet speed\n• "This is a WiFi signal strength issue"\n• Suggest: Move modem to central location, reduce interference\n• Offer: "Would you like me to text you info about WiFi extenders or mesh systems?"'
      },
      {
        id: 19,
        question: "I'm only getting half the speed I'm paying for",
        expected: '• Check what package they have\n• Ask: "Are you testing via WiFi or ethernet cable?"\n• If WiFi: "WiFi speeds are typically 30-50% of plan speed. For full speed, use ethernet"\n• If Ethernet and still slow: Guide through modem reboot, escalate if needed'
      },
      {
        id: 20,
        question: "Everything was fast until today, now it's crawling",
        expected: '• Check service status\n• Ask about recent changes (new devices, moved modem, etc.)\n• Guide through modem reboot\n• Check if multiple devices downloading/streaming\n• Confirm speed improvement after reboot'
      },
      {
        id: 21,
        question: 'My Zoom calls keep freezing',
        expected: '• Check if internet is working overall\n• Ask: "Are other people in your home using the internet at the same time?"\n• Check: "Are you on WiFi or ethernet?"\n• Suggest: Use ethernet for video calls, close other apps/devices during calls\n• If persists: Check internet speed and modem status'
      },
      {
        id: 22,
        question: 'The WiFi app says I have poor signal',
        expected: '• "That\'s a WiFi coverage issue, not your internet connection"\n• Suggest: Move closer to modem, or move modem to central location\n• Check for interference: "Is your modem near other electronics like microwaves or baby monitors?"\n• Offer info on WiFi extenders or mesh systems'
      }
    ]
  },
  {
    category: 'WiFi Issues',
    items: [
      {
        id: 23,
        question: "I can't find my WiFi network",
        expected: '• Check modem type\n• "Is the WiFi light on your modem lit up?"\n• If no WiFi light: "Some modems have a WiFi button - can you press it?"\n• If still no WiFi: Try factory reset or escalate'
      },
      {
        id: 24,
        question: "What's my WiFi password?",
        expected: '• Authenticate customer\n• Get modem info from account\n• "Your WiFi name and password are printed on a label on your modem. Can you check the label?"\n• If label missing: Provide login to modem settings to view password'
      },
      {
        id: 25,
        question: "My WiFi password isn't working",
        expected: '• "Let\'s verify you\'re using the password from the label on your modem"\n• Check for common mistakes: Capital I vs lowercase l, zero vs O\n• Have customer spell it out to verify\n• If still not working: "We may need to check your WiFi password by logging into your modem"\n• Provide modem login instructions'
      },
      {
        id: 26,
        question: 'Can you change my WiFi name and password?',
        expected: '• "Yes! You can change it by logging into your modem"\n• Provide modem IP, username, password based on modem type\n• "Look for Wireless Settings or WiFi Settings"\n• Offer to send portal link for detailed instructions'
      },
      {
        id: 27,
        question: "Some devices connect to WiFi but others don't",
        expected: '• Identify: Device-specific issue, not modem problem\n• "This is usually a problem with the device, not your WiFi"\n• Suggest: Restart the device, forget and reconnect to WiFi\n• Ask: "What type of device isn\'t connecting?" (may need device-specific help)'
      },
      {
        id: 28,
        question: 'My Ring doorbell keeps disconnecting from WiFi',
        expected: '• Check: "Is it far from your modem?"\n• Identify: WiFi signal strength issue - doorbell may be out of range\n• "The doorbell needs strong WiFi signal. Is it possible to move your modem closer or add a WiFi extender?"\n• If modem placement is good: Check for interference or suggest extender'
      }
    ]
  },
  {
    category: 'Phone Service',
    items: [
      {
        id: 29,
        question: 'I have no dial tone on my phone',
        expected: '• Authenticate, check service status\n• Check modem type (phone service via TEL1 port or separate adapter)\n• "Is your phone line plugged into the TEL1 port on your modem?"\n• Try: "Can you dial 6-1-1 to test?" (should say "six, one, one")\n• If no dial tone: Guide through modem reboot\n• Confirm dial tone restored after reboot'
      },
      {
        id: 30,
        question: 'My phone has static on the line',
        expected: '• Check: "Is the phone line plugged securely into the TEL1 port?"\n• Try different phone: "Do you have another phone to test with?"\n• If static persists: Try modem reboot\n• If still static after reboot: "This might be a line issue. Our team can check during business hours 9am-9pm"'
      },
      {
        id: 31,
        question: "I can receive calls but can't make outbound calls",
        expected: '• Check service status (could be billing issue)\n• If active: "Let\'s try rebooting your modem"\n• After reboot: "Try dialing a number now"\n• If still not working: "This might need technical support to check. They\'re available 9am-9pm"'
      },
      {
        id: 32,
        question: "My caller ID isn't showing up",
        expected: '• Check: "Is your phone compatible with caller ID?"\n• "Some older phones don\'t support digital caller ID"\n• Try modem reboot\n• If persists: "This might be a phone compatibility issue or account setting. Our team can check during business hours"'
      },
      {
        id: 33,
        question: "People say they can't hear me on phone calls",
        expected: '• Check: "Does this happen with all phones or just one?"\n• If one phone: "Try a different phone - it might be the handset"\n• If all phones: Check connections, try modem reboot\n• If persists after reboot: Escalate to technical team'
      },
      {
        id: 34,
        question: "My voicemail isn't working",
        expected: '• Ask: "What happens when you try to access voicemail?"\n• Provide voicemail access: "Dial *98 from your home phone"\n• If never set up: "You may need to set it up by calling *98 and following the prompts"\n• If error message: "Our team can check your voicemail settings. They\'re available 9am-9pm"'
      },
      {
        id: 35,
        question: 'Can I add call waiting to my phone service?',
        expected: '• "Yes! Call waiting and other features can be added"\n• "Our sales team can help you add that feature"\n• Provide business hours: "They\'re available 9am-9pm Monday-Friday, 9am-5pm weekends"\n• Provide office number: 519-991-8959'
      }
    ]
  },
  {
    category: 'TV Service',
    items: [
      {
        id: 36,
        question: 'My TV says no signal',
        expected: '• Check what TV service they have (Beaver TV IPTV)\n• "Are you using a set-top box or the Beaver TV app?"\n• Check: "Is your internet working?" (IPTV requires internet)\n• Try: Restart TV box/app, check HDMI/cables, verify TV input\n• If internet down: Fix internet first'
      },
      {
        id: 37,
        question: "I can't log into Beaver TV",
        expected: '• Provide login credentials clearly\n• "Your username is your 10-digit phone number"\n• "Your password is the last 7 digits of your phone number"\n• Give example: "If your number is 519-555-1234, your password would be 5-5-5-1-2-3-4"\n• Try logging in with those credentials\n• If still doesn\'t work: Escalate for password reset'
      },
      {
        id: 38,
        question: 'My TV is buffering constantly',
        expected: '• Check internet connection first (IPTV needs stable internet)\n• "Is your internet working well on other devices?"\n• If internet is slow: Troubleshoot internet issue first\n• If internet is good: Restart TV box/app\n• Check: "Are many people streaming at the same time?"'
      },
      {
        id: 39,
        question: 'Can I add more channels to my TV package?',
        expected: '• "Yes! You can add channels through the customer portal or by calling our team"\n• Offer portal link for self-service channel management\n• Or: "Our team can help you add channels during business hours 9am-9pm at 519-991-8959"'
      },
      {
        id: 40,
        question: "My TV remote isn't working",
        expected: '• Check: "Have you tried replacing the batteries?"\n• Try: Restart the TV box\n• "Point the remote directly at the box - does the box light up when you press buttons?"\n• If box responds but TV doesn\'t: "You may need to reprogram the remote. Our team can walk you through that during business hours"'
      }
    ]
  },
  {
    category: 'Billing & Payments',
    items: [
      {
        id: 41,
        question: 'Why is my service suspended?',
        expected: '• Authenticate, check service status and balance\n• "Your service is temporarily suspended due to an outstanding balance of [amount]"\n• Explain clearly: "Once payment is made, service restores automatically within minutes"\n• Offer portal link: "You can pay online at our customer portal"\n• Provide business hours for payment assistance'
      },
      {
        id: 42,
        question: 'I already paid my bill, why am I still suspended?',
        expected: '• Check payment date: "I see your last payment was on [date]"\n• Check current balance\n• If balance is zero: "Your service should restore within a few minutes. Let\'s wait and I\'ll check again"\n• If still showing balance: Escalate to billing team for payment verification'
      },
      {
        id: 43,
        question: 'How much do I owe?',
        expected: '• Authenticate customer\n• Check outstanding balance\n• Provide amount clearly: "Your current balance is [amount]" OR "Your account has a zero balance"\n• Offer portal link to view detailed billing and payment history'
      },
      {
        id: 44,
        question: 'When is my payment due?',
        expected: '• Authenticate and check account\n• Provide last bill date: "Your last bill was on [date]"\n• Explain billing cycle: "Your billing cycle is monthly, so your next bill should be around [date]"\n• Offer portal link for exact due dates and bill history'
      },
      {
        id: 45,
        question: 'Can I set up automatic payments?',
        expected: '• "Yes! You can set up pre-authorized payments through the customer portal"\n• Offer to text portal link for self-service setup\n• Or: "Our billing team can set that up for you. They\'re available 9am-9pm at 519-991-8959"'
      }
    ]
  },
  {
    category: 'Equipment & Technical',
    items: [
      {
        id: 46,
        question: 'I think my modem is broken',
        expected: '• Ask: "What\'s happening with your modem?"\n• Check service status and confirm reboot was tried\n• If lights are completely off: Check power connections\n• If specific lights are wrong: Troubleshoot based on light pattern\n• If truly defective after troubleshooting: "Our team can arrange an equipment swap. They\'re available 9am-9pm"'
      },
      {
        id: 47,
        question: 'How do I access my modem settings?',
        expected: '• Check modem type from account\n• Provide specific login for their modem:\n  - SmartRG/Kasda: "192.168.1.1, username: admin, password: ekw7566V"\n  - Hitron: "192.168.0.1, username: cusadmin, password: [your WiFi password]"\n• "Open a web browser and type that address in the address bar"'
      },
      {
        id: 48,
        question: 'Can I use my own modem?',
        expected: '• "For DSL service, you need to use our modem"\n• "For Cable internet, you can request to use your own modem, but it needs to be approved"\n• "Our technical team can discuss compatible modems during business hours 9am-9pm"'
      },
      {
        id: 49,
        question: 'My modem keeps restarting by itself',
        expected: '• Check: "Is it plugged into a power bar? Try plugging directly into wall outlet"\n• Check: "Does it restart at specific times or randomly?"\n• If overheating: "Is the modem in an open area with good ventilation?"\n• If persists: "This might be a defective modem. Our team can replace it. They\'re available 9am-9pm"'
      },
      {
        id: 50,
        question: 'I need help setting up a new device on my WiFi',
        expected: '• "I can help with that! What type of device are you connecting?"\n• Provide WiFi credentials: "Your WiFi network name and password are on the label on your modem"\n• Walk through: "On your device, go to WiFi settings, find your network name, enter the password"\n• If specific device issues: Provide general guidance, note that each device is different'
      }
    ]
  }
];

const gradeOptions = [
  { value: 'A+', label: 'A+ (Perfect)', color: '#4caf50' },
  { value: 'A', label: 'A (Great)', color: '#8bc34a' },
  { value: 'B', label: 'B (Good)', color: '#cddc39' },
  { value: 'C', label: 'C (Acceptable)', color: '#ffc107' },
  { value: 'D', label: 'D (Poor)', color: '#ff9800' },
  { value: 'F', label: 'F (Failed)', color: '#f44336' },
  { value: 'N/A', label: 'N/A (Did not test)', color: '#9e9e9e' }
];

// Predefined list of testers
const TESTERS = [
  'Carolyn McGuire',
  'Cosmin Panait',
  'Dan Leoppky',
  'Danielle Bom',
  'Francisco Dominguez',
  'Francis Palad',
  'Jody Schnarr',
  'John Stix',
  'Kier Vergel',
  'Kyle Hebert',
  'Mike Brown',
  'Mike Gallego',
  'Peter Cross',
  'Randy Jheeta',
  'Sandy Colis',
  'Shawn Schmitt',
  'Stephen Hewlett',
  'Guest (Enter your name below)'
];

export default function AITestingForm() {
  const [testerName, setTesterName] = useState('');
  const [guestName, setGuestName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [testingDate, setTestingDate] = useState(new Date().toISOString().split('T')[0]);
  const [testingMethod, setTestingMethod] = useState('');
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<string | false>('panel-0');
  const [testedScenarios, setTestedScenarios] = useState<Set<number>>(new Set());

  // Load tested scenarios from localStorage when tester name changes
  useEffect(() => {
    if (testerName) {
      const storageKey = `fona_testing_${testerName.replace(/\s+/g, '_')}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          setTestedScenarios(new Set(data.testedScenarios || []));
        } catch (e) {
          console.error('Error loading testing history:', e);
        }
      } else {
        setTestedScenarios(new Set());
      }
    }
  }, [testerName]);

  // Calculate progress
  const totalScenarios = scenarios.reduce((sum, cat) => sum + cat.items.length, 0);
  const completedScenarios = Object.keys(responses).filter(key => 
    key.includes('-grade') && responses[key] && responses[key] !== 'N/A'
  ).length;
  const progress = (completedScenarios / totalScenarios) * 100;

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleGradeChange = (scenarioId: number, grade: string) => {
    setResponses({
      ...responses,
      [`${scenarioId}-grade`]: grade
    });
    
    // Mark scenario as tested (if not N/A)
    if (grade !== 'N/A') {
      setTestedScenarios(prev => new Set(prev).add(scenarioId));
    }
  };

  const handleNotesChange = (scenarioId: number, notes: string) => {
    setResponses({
      ...responses,
      [`${scenarioId}-notes`]: notes
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!testerName.trim()) {
      alert('Please select your name');
      return;
    }
    if (testerName.includes('Guest') && !guestName.trim()) {
      alert('Please enter your name in the Guest Name field');
      return;
    }
    if (!phoneNumber.trim()) {
      alert('Please enter the phone number you are testing from');
      return;
    }
    if (!testingMethod) {
      alert('Please select a testing method');
      return;
    }

    setSubmitting(true);

    try {
      // Use guest name if selected, otherwise use dropdown name
      const finalTesterName = testerName.includes('Guest') ? `Guest - ${guestName}` : testerName;
      
      const testResults = {
        tester_name: finalTesterName,
        phone_number: phoneNumber,
        testing_date: testingDate,
        testing_method: testingMethod,
        responses: responses,
        submitted_at: new Date().toISOString(),
        completed_scenarios: completedScenarios,
        total_scenarios: totalScenarios
      };

      const response = await fetch('/api/ai-testing/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testResults),
      });

      if (response.ok) {
        // Save tested scenarios to localStorage
        const storageKey = `fona_testing_${finalTesterName.replace(/\s+/g, '_')}`;
        const currentTested = new Set(testedScenarios);
        
        // Add all newly graded scenarios
        Object.keys(responses).forEach(key => {
          if (key.endsWith('-grade') && responses[key] && responses[key] !== 'N/A') {
            const scenarioId = parseInt(key.replace('-grade', ''));
            currentTested.add(scenarioId);
          }
        });
        
        localStorage.setItem(storageKey, JSON.stringify({
          testerName: finalTesterName,
          testedScenarios: Array.from(currentTested),
          lastUpdated: new Date().toISOString()
        }));
        
        setTestedScenarios(currentTested);
        
        setShowSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setResponses({});
          setPhoneNumber('');
          if (testerName.includes('Guest')) {
            setGuestName('');
          }
          setShowSuccess(false);
          window.scrollTo(0, 0);
        }, 3000);
      } else {
        alert('Error submitting test results. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Error submitting test results. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Fona AI Agent - Quality Testing Survey
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" paragraph>
            Test the Fona AI Agent by calling/texting with each scenario below. Grade the AI's response and provide notes on any issues.
          </Typography>
          <Typography variant="body1">
            📞 <strong>Voice Testing:</strong> Call +1-365-602-0025<br />
            💬 <strong>SMS Testing:</strong> Text +1-365-602-0025
          </Typography>
        </Alert>

        <Box sx={{ mb: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            GRADING SCALE:
          </Typography>
          {gradeOptions.filter(g => g.value !== 'N/A').map(grade => (
            <Typography key={grade.value} sx={{ color: grade.color, fontWeight: 'bold', mb: 0.5 }}>
              {grade.label}
            </Typography>
          ))}
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress: {testedScenarios.size} of {totalScenarios} scenarios tested
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {((testedScenarios.size / totalScenarios) * 100).toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(testedScenarios.size / totalScenarios) * 100} 
            sx={{ height: 10, borderRadius: 5 }} 
          />
        </Box>

        {/* Tester Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            Tester Information
          </Typography>
          
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>Your Name</InputLabel>
            <Select
              value={testerName}
              label="Your Name"
              onChange={(e) => setTesterName(e.target.value)}
            >
              {TESTERS.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {testerName.includes('Guest') && (
            <TextField
              fullWidth
              required
              label="Guest Name (Please enter your name)"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Enter your full name"
            />
          )}

          <TextField
            fullWidth
            required
            label="Phone Number You're Testing From"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g., 519-991-8959 or 5199918959"
            helperText="Enter the phone number you used to call/text the AI (so we can find your test calls)"
          />

          <TextField
            fullWidth
            required
            type="date"
            label="Testing Date"
            value={testingDate}
            onChange={(e) => setTestingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>Testing Method</InputLabel>
            <Select
              value={testingMethod}
              label="Testing Method"
              onChange={(e) => setTestingMethod(e.target.value)}
            >
              <MenuItem value="voice">Voice Call (+1-365-602-0025)</MenuItem>
              <MenuItem value="sms">SMS Chat (+1-365-602-0025)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Scenarios */}
        {scenarios.map((category, catIndex) => (
          <Accordion
            key={catIndex}
            expanded={expanded === `panel-${catIndex}`}
            onChange={handleAccordionChange(`panel-${catIndex}`)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', flexGrow: 1 }}>
                  {category.category} ({category.items.length} scenarios)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                  {category.items.filter(item => testedScenarios.has(item.id)).length} / {category.items.length} completed
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {category.items.map((scenario) => {
                const isTested = testedScenarios.has(scenario.id);
                
                return (
                <Paper 
                  key={scenario.id} 
                  elevation={isTested ? 2 : 1} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    bgcolor: isTested ? '#e8f5e9' : '#fafafa',
                    border: isTested ? '2px solid #4caf50' : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {isTested ? (
                      <Chip 
                        icon={<CheckBoxIcon />} 
                        label="Previously Tested" 
                        color="success" 
                        size="small" 
                        sx={{ mr: 2 }} 
                      />
                    ) : (
                      <Chip 
                        icon={<CheckBoxOutlineBlankIcon />} 
                        label="Not Yet Tested" 
                        variant="outlined" 
                        size="small" 
                        sx={{ mr: 2 }} 
                      />
                    )}
                  </Box>
                  
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    Scenario {scenario.id}: "{scenario.question}"
                  </Typography>

                  <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      ✅ Expected AI Behavior:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {scenario.expected}
                    </Typography>
                  </Box>

                  <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                    <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Grade the AI's performance:
                    </FormLabel>
                    <RadioGroup
                      value={responses[`${scenario.id}-grade`] || ''}
                      onChange={(e) => handleGradeChange(scenario.id, e.target.value)}
                    >
                      {gradeOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={
                            <Typography sx={{ color: option.color, fontWeight: 'bold' }}>
                              {option.label}
                            </Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes/Issues (Optional)"
                    placeholder="Describe any issues, concerns, or observations..."
                    value={responses[`${scenario.id}-notes`] || ''}
                    onChange={(e) => handleNotesChange(scenario.id, e.target.value)}
                  />

                  {responses[`${scenario.id}-grade`] && responses[`${scenario.id}-grade`] !== 'N/A' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, color: '#4caf50' }}>
                      <CheckCircleIcon sx={{ mr: 1 }} />
                      <Typography variant="body2">Scenario completed</Typography>
                    </Box>
                  )}
                </Paper>
                );
              })}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Submit Button */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting || completedScenarios === 0}
            sx={{ 
              px: 6, 
              py: 2, 
              fontSize: '1.2rem',
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' }
            }}
          >
            {submitting ? 'Submitting...' : `Submit Test Results (${completedScenarios} scenarios completed)`}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can submit multiple times - test as many scenarios as you'd like!
          </Typography>
        </Box>
      </Paper>

      {/* Success Notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Test results submitted successfully! Thank you for your feedback.
        </Alert>
      </Snackbar>
    </Container>
  );
}

